import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/ui/useIsMobile";
import type { ConversationFollowUpSheetProps } from "@/types/voice/conversation-follow-up";

export const ConversationFollowUpSheet = ({ open, onClose, form, onSubmit, isPending, isSuccess, error, purpose = "follow_up", contextTruncated = false }: ConversationFollowUpSheetProps) => {
  const isMobile = useIsMobile(768);
  const isSummary = purpose === "summary";
  return (
  <BaseSheet
    isOpen={open}
    onClose={onClose}
    title={isSummary ? "Email me a summary" : "Continue over email"}
    description={isSummary
      ? "Ask this Genie or its team for an email recap of your conversation. You can keep chatting."
      : "Share your email so the Genie can continue this conversation with you over email."}
    size="md"
    side={isMobile ? "bottom" : "right"}
    overlayClassName="z-[11990]"
    contentClassName={`z-[12000] [&_button]:min-h-[44px] [&_button]:min-w-[44px] ${isMobile ? "!max-h-[90dvh] rounded-t-2xl" : "h-[100dvh]"}`}
    footer={
      <div className="flex w-full justify-start gap-3 pb-[env(safe-area-inset-bottom)]">
        <Button variant="outline" onClick={onClose}>{isSuccess ? "Done" : "Keep chatting"}</Button>
        {!isSuccess && (
          <Button type="submit" form="conversation-email-follow-up" disabled={isPending}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary text-white font-semibold shadow-lg shadow-genie-primary/30">
            {isPending ? "Sending…" : isSummary ? "Request email summary" : "Send request"}
          </Button>
        )}
      </div>
    }
  >
    {isSuccess ? (
      <div role="status" className="rounded-xl border border-genie-border bg-white p-5">
        <h3 className="font-semibold text-[#222B45]">{isSummary ? "Your summary request has been sent" : "Your request has been sent"}</h3>
        <p className="mt-2 break-words text-sm text-gray-600">{isSummary
          ? `The Genie or its team has your recap request and can reply to ${form.getValues("email").trim()}. You can keep chatting while you wait.`
          : `The Genie has your recent conversation and will continue the discussion with you over email at ${form.getValues("email").trim()}.`}</p>
      </div>
    ) : (
      <form id="conversation-email-follow-up" onSubmit={onSubmit} className="space-y-4">
        {isSummary && (
          <p className="text-sm text-gray-600">
            {contextTruncated
              ? "This is a long conversation, so the request includes only the most recent messages. The recap will cover those messages."
              : "The request includes the conversation up to when you opened this form."}
          </p>
        )}
        <div>
          <label htmlFor="conversation-follow-up-email" className="mb-2 block text-sm font-semibold text-gray-700">Your email</label>
          <input id="conversation-follow-up-email" type="email" autoComplete="email" maxLength={254}
            disabled={isPending} aria-invalid={!!form.formState.errors.email}
            aria-describedby={form.formState.errors.email ? "conversation-follow-up-email-error" : undefined}
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-genie-primary"
            {...form.register("email", { required: "Enter your email address.", validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) || "Enter a valid email address." })}
          />
          {form.formState.errors.email && <p id="conversation-follow-up-email-error" role="alert" className="mt-2 text-sm text-red-600">{form.formState.errors.email.message}</p>}
        </div>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </form>
    )}
  </BaseSheet>
  );
};
